package com.eladfinish.bible.contest.screens.search.ui.dialogs

import androidx.compose.foundation.clickable
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.lazy.LazyColumn
import androidx.compose.foundation.lazy.itemsIndexed
import androidx.compose.foundation.lazy.rememberLazyListState
import androidx.compose.material.icons.Icons
import androidx.compose.material.icons.filled.MoreVert
import androidx.compose.material3.AlertDialog
import androidx.compose.material3.DropdownMenu
import androidx.compose.material3.DropdownMenuItem
import androidx.compose.material3.HorizontalDivider
import androidx.compose.material3.Icon
import androidx.compose.material3.IconButton
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.SegmentedButton
import androidx.compose.material3.SegmentedButtonDefaults
import androidx.compose.material3.SingleChoiceSegmentedButtonRow
import androidx.compose.material3.Text
import androidx.compose.material3.TextButton
import androidx.compose.runtime.Composable
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.saveable.rememberSaveable
import androidx.compose.runtime.setValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.platform.testTag
import androidx.compose.ui.text.style.TextDirection
import androidx.compose.ui.text.style.TextOverflow
import androidx.compose.ui.unit.dp
import com.eladfinish.bible.contest.SharedRes
import com.eladfinish.bible.contest.cancel
import com.eladfinish.bible.contest.close
import com.eladfinish.bible.contest.screens.search.history.SearchHistoryEntry
import com.eladfinish.bible.contest.screens.search.history.SearchHistoryKey
import com.eladfinish.bible.contest.screens.search.history.SearchHistoryKind
import com.eladfinish.bible.contest.screens.search.history.SearchHistorySort
import com.eladfinish.bible.contest.screens.search.mvi.SearchAction
import com.eladfinish.bible.contest.search_history
import com.eladfinish.bible.contest.search_history_clear_all
import com.eladfinish.bible.contest.search_history_clear_confirm_message
import com.eladfinish.bible.contest.search_history_clear_confirm_title
import com.eladfinish.bible.contest.search_history_delete_entry
import com.eladfinish.bible.contest.search_history_empty
import com.eladfinish.bible.contest.search_history_most_searched
import com.eladfinish.bible.contest.search_history_recent
import com.eladfinish.bible.contest.search_history_without_nikud
import com.eladfinish.bible.contest.search_history_without_teamim
import com.eladfinish.bible.contest.text_only_qri
import com.eladfinish.bible.contest.toolbar_menu_more_options
import com.eladfinish.bible.contest.ui.components.dialogs.BasicDialogWithTitle
import com.eladfinish.bible.contest.ui.components.mouseVerticalScrollbar
import com.eladfinish.bible.contest.ui.icons.AppIcons
import dev.icerock.moko.resources.StringResource
import dev.icerock.moko.resources.compose.localized
import dev.icerock.moko.resources.compose.stringResource
import dev.icerock.moko.resources.desc.desc
import kotlinx.collections.immutable.ImmutableList

const val SEARCH_HISTORY_ROW_TEST_TAG = "search_history_row"

@Composable
internal fun SearchHistoryDialog(
    entries: ImmutableList<SearchHistoryEntry>,
    sort: SearchHistorySort,
    isConfirmingClear: Boolean,
    onAction: (SearchAction) -> Unit,
    onDismiss: () -> Unit,
) {
    BasicDialogWithTitle(
        onDismiss = onDismiss,
        title = stringResource(SharedRes.strings.search_history),
        onCancel = onDismiss,
        cancelText = SharedRes.strings.close.desc(),
        onAction = { onAction(SearchAction.ClearHistoryClicked) }.takeIf { entries.isNotEmpty() },
        actionText = SharedRes.strings.search_history_clear_all.desc(),
        content = {
            if (entries.isEmpty()) {
                EmptyHistory()
            } else {
                SortSelector(
                    selected = sort,
                    onSelected = { onAction(SearchAction.HistorySortChanged(it)) },
                )

                val listState = rememberLazyListState()

                LazyColumn(
                    state = listState,
                    modifier = Modifier.mouseVerticalScrollbar(listState),
                ) {
                    itemsIndexed(
                        items = entries,
                        // a String: on Android a lazy-list key has to be saveable in a Bundle
                        key = { _, entry -> entry.key.toString() },
                    ) { index, entry ->
                        HistoryRow(
                            number = index + 1,
                            entry = entry,
                            onClick = { onAction(SearchAction.HistoryEntryClicked(entry)) },
                            onDelete = { onAction(SearchAction.HistoryEntryDeleteClicked(entry)) },
                        )
                        HorizontalDivider()
                    }
                }
            }
        },
    )

    if (isConfirmingClear) {
        val onCancel = { onAction(SearchAction.ClearHistoryCancelled) }

        AlertDialog(
            onDismissRequest = onCancel,
            title = {
                Text(stringResource(SharedRes.strings.search_history_clear_confirm_title))
            },
            text = {
                Text(stringResource(SharedRes.strings.search_history_clear_confirm_message))
            },
            confirmButton = {
                TextButton(onClick = { onAction(SearchAction.ClearHistoryConfirmed) }) {
                    Text(
                        text = stringResource(SharedRes.strings.search_history_clear_all),
                        color = MaterialTheme.colorScheme.error,
                    )
                }
            },
            dismissButton = {
                TextButton(onClick = onCancel) {
                    Text(stringResource(SharedRes.strings.cancel))
                }
            },
        )
    }
}

@Composable
private fun SortSelector(
    selected: SearchHistorySort,
    onSelected: (SearchHistorySort) -> Unit,
) {
    val options = listOf(
        SearchHistorySort.RECENT to SharedRes.strings.search_history_recent,
        SearchHistorySort.MOST_SEARCHED to SharedRes.strings.search_history_most_searched,
    )

    SingleChoiceSegmentedButtonRow(
        modifier = Modifier
            .fillMaxWidth()
            .padding(horizontal = 16.dp, vertical = 8.dp),
    ) {
        options.forEachIndexed { index, (sort, label) ->
            SegmentedButton(
                selected = sort == selected,
                onClick = { onSelected(sort) },
                shape = SegmentedButtonDefaults.itemShape(index = index, count = options.size),
            ) {
                Text(
                    text = stringResource(label),
                    maxLines = 1,
                )
            }
        }
    }
}

@Composable
private fun HistoryRow(
    number: Int,
    entry: SearchHistoryEntry,
    onClick: () -> Unit,
    onDelete: () -> Unit,
) {
    Row(
        modifier = Modifier
            .fillMaxWidth()
            .clickable(onClick = onClick)
            .testTag(SEARCH_HISTORY_ROW_TEST_TAG)
            .padding(start = 16.dp, top = 4.dp, bottom = 4.dp),
        verticalAlignment = Alignment.CenterVertically,
    ) {
        Text(
            text = "$number.",
            modifier = Modifier.padding(end = 12.dp),
            style = MaterialTheme.typography.titleMedium,
            color = MaterialTheme.colorScheme.onSurfaceVariant,
        )

        Column(
            modifier = Modifier.weight(1f),
            verticalArrangement = Arrangement.spacedBy(2.dp),
        ) {
            Text(
                text = entry.key.query,
                modifier = Modifier.fillMaxWidth(),
                style = MaterialTheme.typography.titleMedium.copy(
                    textDirection = TextDirection.ContentOrRtl,
                ),
                maxLines = 1,
                overflow = TextOverflow.Ellipsis,
            )

            val details = entry.detailsLine()
            if (details.isNotEmpty()) {
                Text(
                    text = details,
                    style = MaterialTheme.typography.bodySmall,
                    color = MaterialTheme.colorScheme.onSurfaceVariant,
                    maxLines = 2,
                    overflow = TextOverflow.Ellipsis,
                )
            }
        }

        // Delete sits behind a menu: a bare X on the row read as "close the dialog".
        Box {
            var isMenuOpen by rememberSaveable { mutableStateOf(false) }

            IconButton(onClick = { isMenuOpen = true }) {
                Icon(
                    imageVector = Icons.Filled.MoreVert,
                    contentDescription = stringResource(SharedRes.strings.toolbar_menu_more_options),
                    tint = MaterialTheme.colorScheme.onSurfaceVariant,
                )
            }

            DropdownMenu(
                expanded = isMenuOpen,
                onDismissRequest = { isMenuOpen = false },
            ) {
                DropdownMenuItem(
                    text = { Text(stringResource(SharedRes.strings.search_history_delete_entry)) },
                    onClick = {
                        isMenuOpen = false
                        onDelete()
                    },
                )
            }
        }
    }
}

// Only what differs from a plain search is worth a label, so most rows stay a single line.
@Composable
private fun SearchHistoryEntry.detailsLine(): String {
    val labels = key.nonDefaultOptionLabels().map { stringResource(it) }
    // isolated as left-to-right so it reads "×4" inside a right-to-left line too
    val count = "\u2066×$searchCount\u2069".takeIf { searchCount > 1 }

    return (labels + listOfNotNull(count)).joinToString(separator = " · ")
}

internal fun SearchHistoryKey.nonDefaultOptionLabels(): List<StringResource> =
    buildList {
        val isRegular = kind == SearchHistoryKind.REGULAR

        if (isRegular && searchMode != SearchHistoryKey.DEFAULT_SEARCH_MODE) {
            add(searchMode.stringResource)
        }
        if (isRegular && searchPlace != SearchHistoryKey.DEFAULT_SEARCH_PLACE) {
            add(searchPlace.stringResource)
        }
        if (onlyQri != SearchHistoryKey.DEFAULT_ONLY_QRI) {
            add(SharedRes.strings.text_only_qri)
        }
        if (isRegular && !withNikud) {
            add(SharedRes.strings.search_history_without_nikud)
        }
        if (isRegular && !withTeamim) {
            add(SharedRes.strings.search_history_without_teamim)
        }
    }

@Composable
private fun EmptyHistory() {
    Column(
        modifier = Modifier
            .fillMaxWidth()
            .padding(horizontal = 16.dp, vertical = 32.dp),
        horizontalAlignment = Alignment.CenterHorizontally,
        verticalArrangement = Arrangement.spacedBy(12.dp),
    ) {
        Icon(
            imageVector = AppIcons.History,
            contentDescription = null,
            modifier = Modifier.size(48.dp),
            tint = MaterialTheme.colorScheme.onSurfaceVariant,
        )
        Text(
            text = stringResource(SharedRes.strings.search_history_empty),
            style = MaterialTheme.typography.bodyLarge,
            color = MaterialTheme.colorScheme.onSurfaceVariant,
        )
    }
}
